// Internationalization module for ChatWidget

// Detect user's language from browser settings
export function detectLanguage(i18nData) {
    // Check localStorage first for user preference
    const savedLang = localStorage.getItem('chat_widget_language');
    if (savedLang && i18nData.errorMessages[savedLang]) {
        return savedLang;
    }

    // Fallback to browser language
    const browserLang = navigator.language || navigator.userLanguage;

    // Handle case where navigator language is not available
    if (!browserLang) {
        return 'en';
    }

    const langCode = browserLang.split('-')[0]; // Get 'en' from 'en-US'

    // Return if supported, otherwise default to 'en'
    return i18nData.errorMessages[langCode] ? langCode : 'en';
}

// Internationalization translations
export const i18n = {
    errorMessages: {
        en: {
            // Generic errors
            'NETWORK_ERROR': 'Unable to connect to the server. Please check your connection and try again.',
            'UNKNOWN_ERROR': 'An unexpected error occurred. Please try again.',
            'GENERIC_ERROR': 'Sorry, something went wrong. Please try again.',

            // Bad Request errors (400)
            'INVALID_INPUT': 'Please check your input and try again.',
            'INVALID_JSON': 'Invalid data format. Please try again.',
            'MISSING_FIELD': 'Please fill in all required fields.',
            'QUERY_TOO_LONG': 'Your message is too long. Please shorten it and try again.',
            'QUERY_EMPTY': 'Please enter a message.',
            'INVALID_SESSION_ID': 'Your session has expired. Please refresh the page.',

            // Unauthorized errors (401)
            'UNAUTHORIZED': 'Authentication required. Please log in.',
            'INVALID_CREDENTIALS': 'Invalid username or password.',
            'SESSION_EXPIRED': 'Your session has expired. Please refresh the page.',

            // Forbidden errors (403)
            'FORBIDDEN': 'You do not have permission to perform this action.',
            'INSUFFICIENT_PERMISSIONS': 'You do not have sufficient permissions.',

            // Not Found errors (404)
            'NOT_FOUND': 'The requested resource was not found.',
            'SESSION_NOT_FOUND': 'Your session was not found. Please refresh the page.',
            'DOCUMENT_NOT_FOUND': 'Document not found.',
            'USER_NOT_FOUND': 'User not found.',

            // Method Not Allowed (405)
            'METHOD_NOT_ALLOWED': 'This operation is not allowed.',

            // Conflict errors (409)
            'CONFLICT': 'This request conflicts with existing data.',
            'USER_EXISTS': 'This user already exists.',
            'DUPLICATE_REQUEST': 'This information has already been submitted.',

            // Payload Too Large (413)
            'PAYLOAD_TOO_LARGE': 'Your message is too large. Please shorten it.',

            // Rate Limiting (429)
            'RATE_LIMITED': 'Too many requests. Please wait a moment and try again.',
            'QUERY_LIMIT_EXCEEDED': 'You have exceeded the query limit. Please try again later.',

            // Internal Server errors (500)
            'INTERNAL_ERROR': 'A server error occurred. Please try again later.',
            'DATABASE_ERROR': 'A database error occurred. Please try again.',
            'RAG_ERROR': 'Unable to process your query. Please try again.',
            'EMBEDDING_ERROR': 'Unable to process your message. Please try again.',
            'EMAIL_ERROR': 'Unable to send email. Please try again later.',
            'TEMPLATE_ERROR': 'Unable to process template. Please try again.',

            // Service Unavailable (503)
            'SERVICE_UNAVAILABLE': 'The service is temporarily unavailable. Please try again later.',
            'EMAIL_NOT_CONFIGURED': 'Email service is not configured.',

            // Field-specific validation messages
            'VALIDATION_FIRST_NAME_REQUIRED': 'Please enter your first name.',
            'VALIDATION_LAST_NAME_REQUIRED': 'Please enter your last name.',
            'VALIDATION_PHONE_OR_EMAIL_REQUIRED': 'Please provide either a phone number or email address.',
            'VALIDATION_PHONE_INVALID': 'Please enter a valid 10-digit phone number.',
            'VALIDATION_EMAIL_INVALID': 'Please enter a valid email address.',
            'VALIDATION_DATE_REQUIRED': 'Please select a date.',
            'VALIDATION_TIME_REQUIRED': 'Please select a time.',
            'VALIDATION_CONSENT_REQUIRED': 'Please accept the terms to continue.',

            // Success messages
            'SUBMIT_SUCCESS': 'Thank you! Your information has been received.',
            'APPOINTMENT_SUCCESS': 'Your appointment request has been submitted.',
            'CONVERSATION_SENT': 'Your conversation has been sent successfully.',

            // Conversation flow validation messages
            'please provide all the required information': 'Please provide all the required information.',
            'please provide your full name': 'Please provide your full name.',
            'please provide your email address': 'Please provide your email address.',
            'please provide your phone number': 'Please provide your phone number.',
            'please provide a valid email address': 'Please provide a valid email address.',
            'please provide a valid email address (example: name@example.com)': 'Please provide a valid email address (example: name@example.com).',
            'please provide a valid phone number': 'Please provide a valid phone number.',
            'please provide a 10-digit phone number': 'Please provide a 10-digit phone number.',

            // Form labels and placeholders
            'FORM_YOUR_NAME': 'Your Name *',
            'FORM_PHONE_PLACEHOLDER': '(555) 123-4567',
            'FORM_EMAIL_PLACEHOLDER': 'your@email.com',
            'FORM_NAME_REQUIRED': 'Name is required',
            'FORM_PHONE_OR_EMAIL_REQUIRED': 'Phone or email required',
            'FORM_PHONE_REQUIRED': 'Phone is required',
            'FORM_EMAIL_REQUIRED': 'Email is required',
            'FORM_INVALID_PHONE': 'Invalid phone number',
            'FORM_INVALID_EMAIL': 'Invalid email',
            'FORM_DECLINE': 'Decline',
            'FORM_SEND': 'Send',
            'FORM_CANCEL': 'Cancel',
            'FORM_UPDATE': 'Update',
            'FORM_CONFIRM': 'Yes, it\'s correct',
            'FORM_CONTACT_ON_FILE': 'We have your contact information on file. Is this still correct?',
            'FORM_NAME': 'Name:',
            'FORM_EMAIL': 'Email:',
            'FORM_PHONE': 'Phone:',

            // Appointment form
            'FORM_APPOINTMENT_INTRO': 'Please select your preferred date and time for the appointment.',
            'FORM_PREFERRED_DATE': 'Preferred Date *',
            'FORM_PREFERRED_TIME': 'Preferred Time *',
            'FORM_ADDITIONAL_NOTES': 'Additional Notes (Optional)',
            'FORM_NOTES_PLACEHOLDER': 'Any additional details or questions?',
            'FORM_CONTACT_INFO_INTRO': 'Please provide your contact information:',
            'FORM_FIRST_NAME': 'First Name *',
            'FORM_LAST_NAME': 'Last Name *',
            'FORM_PHONE_NUMBER': 'Phone Number',
            'FORM_EMAIL_ADDRESS': 'Email Address',
            'FORM_PHONE_OR_EMAIL_ERROR': 'Please provide either a phone number or email address.',
            'FORM_DATE_REQUIRED': 'Date is required',
            'FORM_TIME_REQUIRED': 'Time is required',
            'FORM_FIRST_NAME_REQUIRED': 'First name is required',
            'FORM_LAST_NAME_REQUIRED': 'Last name is required',
            'FORM_VALID_PHONE': 'Please enter a valid 10-digit phone number',
            'FORM_VALID_EMAIL': 'Please enter a valid email address',
            'FORM_SCHEDULE_APPOINTMENT': 'Schedule Appointment',

            // Contact Us form
            'FORM_CONTACT_US_TITLE': 'Contact Us',
            'FORM_CONTACT_US_MESSAGE': 'Your Message *',
            'FORM_MESSAGE_PLACEHOLDER': 'Your message...',
            'FORM_MESSAGE_REQUIRED': 'Message is required',
            'FORM_NAME_LABEL': 'Name',
            'FORM_EMAIL_LABEL': 'Email',
            'FORM_PHONE_LABEL': 'Phone',
            'FORM_MESSAGE_LABEL': 'Message',
            'FORM_CONTACT_REQUIRED_NOTE': '* Either email or phone is required',
            'FORM_SEND_MESSAGE': 'Send Message',
            'FORM_CONSENT_LABEL': 'I consent to be contacted',
            'FORM_CONSENT_REQUIRED': 'Please check the consent box to continue',
            'NOTIFICATION_SENT': 'Your message has been sent. We will be in touch shortly.',
            'NOTIFICATION_FAILED': 'We\'ve saved your details but couldn\'t send the notification automatically. Please contact us directly.'
        },
        fr: {
            // Generic errors
            'NETWORK_ERROR': 'Impossible de se connecter au serveur. Veuillez vérifier votre connexion et réessayer.',
            'UNKNOWN_ERROR': 'Une erreur inattendue s\'est produite. Veuillez réessayer.',
            'GENERIC_ERROR': 'Désolé, quelque chose s\'est mal passé. Veuillez réessayer.',

            // Bad Request errors (400)
            'INVALID_INPUT': 'Veuillez vérifier votre saisie et réessayer.',
            'INVALID_JSON': 'Format de données invalide. Veuillez réessayer.',
            'MISSING_FIELD': 'Veuillez remplir tous les champs obligatoires.',
            'QUERY_TOO_LONG': 'Votre message est trop long. Veuillez le raccourcir et réessayer.',
            'QUERY_EMPTY': 'Veuillez entrer un message.',
            'INVALID_SESSION_ID': 'Votre session a expiré. Veuillez actualiser la page.',

            // Unauthorized errors (401)
            'UNAUTHORIZED': 'Authentification requise. Veuillez vous connecter.',
            'INVALID_CREDENTIALS': 'Nom d\'utilisateur ou mot de passe invalide.',
            'SESSION_EXPIRED': 'Votre session a expiré. Veuillez actualiser la page.',

            // Forbidden errors (403)
            'FORBIDDEN': 'Vous n\'avez pas la permission d\'effectuer cette action.',
            'INSUFFICIENT_PERMISSIONS': 'Vous n\'avez pas les permissions suffisantes.',

            // Not Found errors (404)
            'NOT_FOUND': 'La ressource demandée n\'a pas été trouvée.',
            'SESSION_NOT_FOUND': 'Votre session n\'a pas été trouvée. Veuillez actualiser la page.',
            'DOCUMENT_NOT_FOUND': 'Document non trouvé.',
            'USER_NOT_FOUND': 'Utilisateur non trouvé.',

            // Method Not Allowed (405)
            'METHOD_NOT_ALLOWED': 'Cette opération n\'est pas autorisée.',

            // Conflict errors (409)
            'CONFLICT': 'Cette demande est en conflit avec les données existantes.',
            'USER_EXISTS': 'Cet utilisateur existe déjà.',
            'DUPLICATE_REQUEST': 'Ces informations ont déjà été soumises.',

            // Payload Too Large (413)
            'PAYLOAD_TOO_LARGE': 'Votre message est trop volumineux. Veuillez le raccourcir.',

            // Rate Limiting (429)
            'RATE_LIMITED': 'Trop de requêtes. Veuillez attendre un moment et réessayer.',
            'QUERY_LIMIT_EXCEEDED': 'Vous avez dépassé la limite de requêtes. Veuillez réessayer plus tard.',

            // Internal Server errors (500)
            'INTERNAL_ERROR': 'Une erreur serveur s\'est produite. Veuillez réessayer plus tard.',
            'DATABASE_ERROR': 'Une erreur de base de données s\'est produite. Veuillez réessayer.',
            'RAG_ERROR': 'Impossible de traiter votre requête. Veuillez réessayer.',
            'EMBEDDING_ERROR': 'Impossible de traiter votre message. Veuillez réessayer.',
            'EMAIL_ERROR': 'Impossible d\'envoyer l\'e-mail. Veuillez réessayer plus tard.',
            'TEMPLATE_ERROR': 'Impossible de traiter le modèle. Veuillez réessayer.',

            // Service Unavailable (503)
            'SERVICE_UNAVAILABLE': 'Le service est temporairement indisponible. Veuillez réessayer plus tard.',
            'EMAIL_NOT_CONFIGURED': 'Le service de messagerie n\'est pas configuré.',

            // Field-specific validation messages
            'VALIDATION_FIRST_NAME_REQUIRED': 'Veuillez entrer votre prénom.',
            'VALIDATION_LAST_NAME_REQUIRED': 'Veuillez entrer votre nom de famille.',
            'VALIDATION_PHONE_OR_EMAIL_REQUIRED': 'Veuillez fournir un numéro de téléphone ou une adresse e-mail.',
            'VALIDATION_PHONE_INVALID': 'Veuillez entrer un numéro de téléphone valide à 10 chiffres.',
            'VALIDATION_EMAIL_INVALID': 'Veuillez entrer une adresse e-mail valide.',
            'VALIDATION_DATE_REQUIRED': 'Veuillez sélectionner une date.',
            'VALIDATION_TIME_REQUIRED': 'Veuillez sélectionner une heure.',
            'VALIDATION_CONSENT_REQUIRED': 'Veuillez accepter les conditions pour continuer.',

            // Success messages
            'SUBMIT_SUCCESS': 'Merci ! Vos informations ont été reçues.',
            'APPOINTMENT_SUCCESS': 'Votre demande de rendez-vous a été soumise.',
            'CONVERSATION_SENT': 'Votre conversation a été envoyée avec succès.',

            // Conversation flow validation messages
            'please provide all the required information': 'Veuillez fournir toutes les informations requises.',
            'please provide your full name': 'Veuillez fournir votre nom complet.',
            'please provide your email address': 'Veuillez fournir votre adresse e-mail.',
            'please provide your phone number': 'Veuillez fournir votre numéro de téléphone.',
            'please provide a valid email address': 'Veuillez fournir une adresse e-mail valide.',
            'please provide a valid email address (example: name@example.com)': 'Veuillez fournir une adresse e-mail valide (exemple : nom@exemple.com).',
            'please provide a valid phone number': 'Veuillez fournir un numéro de téléphone valide.',
            'please provide a 10-digit phone number': 'Veuillez fournir un numéro de téléphone à 10 chiffres.',

            // Form labels and placeholders
            'FORM_YOUR_NAME': 'Votre nom *',
            'FORM_PHONE_PLACEHOLDER': '(555) 123-4567',
            'FORM_EMAIL_PLACEHOLDER': 'votre@courriel.com',
            'FORM_NAME_REQUIRED': 'Le nom est requis',
            'FORM_PHONE_OR_EMAIL_REQUIRED': 'Téléphone ou courriel requis',
            'FORM_PHONE_REQUIRED': 'Le téléphone est requis',
            'FORM_EMAIL_REQUIRED': 'Le courriel est requis',
            'FORM_INVALID_PHONE': 'Numéro de téléphone invalide',
            'FORM_INVALID_EMAIL': 'Courriel invalide',
            'FORM_DECLINE': 'Refuser',
            'FORM_SEND': 'Envoyer',
            'FORM_CANCEL': 'Annuler',
            'FORM_UPDATE': 'Modifier',
            'FORM_CONFIRM': 'Oui, c\'est correct',
            'FORM_CONTACT_ON_FILE': 'Nous avons vos coordonnées en dossier. Est-ce toujours exact?',
            'FORM_NAME': 'Nom:',
            'FORM_EMAIL': 'Courriel:',
            'FORM_PHONE': 'Téléphone:',

            // Appointment form
            'FORM_APPOINTMENT_INTRO': 'Veuillez sélectionner la date et l\'heure souhaitées pour le rendez-vous.',
            'FORM_PREFERRED_DATE': 'Date souhaitée *',
            'FORM_PREFERRED_TIME': 'Heure souhaitée *',
            'FORM_ADDITIONAL_NOTES': 'Notes supplémentaires (facultatif)',
            'FORM_NOTES_PLACEHOLDER': 'Des détails ou questions supplémentaires?',
            'FORM_CONTACT_INFO_INTRO': 'Veuillez fournir vos coordonnées:',
            'FORM_FIRST_NAME': 'Prénom *',
            'FORM_LAST_NAME': 'Nom de famille *',
            'FORM_PHONE_NUMBER': 'Numéro de téléphone',
            'FORM_EMAIL_ADDRESS': 'Adresse courriel',
            'FORM_PHONE_OR_EMAIL_ERROR': 'Veuillez fournir un numéro de téléphone ou une adresse courriel.',
            'FORM_DATE_REQUIRED': 'La date est requise',
            'FORM_TIME_REQUIRED': 'L\'heure est requise',
            'FORM_FIRST_NAME_REQUIRED': 'Le prénom est requis',
            'FORM_LAST_NAME_REQUIRED': 'Le nom de famille est requis',
            'FORM_VALID_PHONE': 'Veuillez entrer un numéro de téléphone valide à 10 chiffres',
            'FORM_VALID_EMAIL': 'Veuillez entrer une adresse courriel valide',
            'FORM_SCHEDULE_APPOINTMENT': 'Prendre rendez-vous',

            // Contact Us form
            'FORM_CONTACT_US_TITLE': 'Nous contacter',
            'FORM_CONTACT_US_MESSAGE': 'Votre message *',
            'FORM_MESSAGE_PLACEHOLDER': 'Votre message...',
            'FORM_MESSAGE_REQUIRED': 'Le message est requis',
            'FORM_NAME_LABEL': 'Nom',
            'FORM_EMAIL_LABEL': 'Courriel',
            'FORM_PHONE_LABEL': 'Téléphone',
            'FORM_MESSAGE_LABEL': 'Message',
            'FORM_CONTACT_REQUIRED_NOTE': '* Courriel ou téléphone requis',
            'FORM_SEND_MESSAGE': 'Envoyer le message',
            'FORM_CONSENT_LABEL': 'J\'accepte d\'être contacté(e)',
            'FORM_CONSENT_REQUIRED': 'Veuillez cocher la case de consentement pour continuer',
            'NOTIFICATION_SENT': 'Votre message a été envoyé. Nous vous contacterons sous peu.',
            'NOTIFICATION_FAILED': 'Nous avons enregistré vos coordonnées, mais n\'avons pas pu envoyer la notification automatiquement. Veuillez nous contacter directement.'
        }
    }
};

// Get translated error message
export function t(errorCode, language, fallback = null) {
    const messages = i18n.errorMessages[language] || i18n.errorMessages.en;
    return messages[errorCode] || fallback || messages['GENERIC_ERROR'];
}

// Set language and save preference
export function setLanguage(langCode) {
    if (i18n.errorMessages[langCode]) {
        localStorage.setItem('chat_widget_language', langCode);
        return langCode;
    }
    return null;
}

// Translate validation error messages
export function translateValidationError(message, language) {
    // Check if this is a validation error from the backend
    const validationPrefix = 'I need valid information: ';
    if (message && message.startsWith(validationPrefix)) {
        // Extract the validation message part
        const validationMessage = message.substring(validationPrefix.length).toLowerCase();

        // Try to translate it
        const translated = t(validationMessage, language);

        // If we got a translation (not the fallback generic error), use it
        if (translated && translated !== t('GENERIC_ERROR', language)) {
            return translated;
        }

        // Otherwise, return the original message with just the validation part
        return message.substring(validationPrefix.length);
    }

    // Not a validation error, return as-is
    return message;
}
